import { Badge } from "@/components/ui/badge"
import {
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  CircleX,
  CreditCard,
  FileCheck2,
  Hammer,
  Layers3,
  PackageCheck,
  Palette,
  PauseCircle,
  SearchCheck,
  ShieldCheck,
} from "lucide-react"
import { cn } from "@/lib/utils"

// Status pill — one of four calm semantic tiers (design/docs/DECISIONS.md
// §Color). The `tier` drives the color; the `label` is the customer-facing
// phrase. A filled dot in the current text color echoes the prototype pill.
//
// Tiers map to the shadcn Badge variants seeded in components/ui/badge.jsx:
//   needs_you  → amber  (your move: approve / review)
//   in_progress→ blue   (being written / designed / in production)
//   done       → green  (delivered / live)
//   hold       → gray   (on hold / scheduled)
const TIER_VARIANT = {
  needs_you: "needsYou",
  in_progress: "progress",
  done: "done",
  hold: "hold",
}

const PHASE_ICON = {
  payment: CreditCard,
  scheduled: CalendarClock,
  production: Hammer,
  draft: FileCheck2,
  scan: ShieldCheck,
  design: Palette,
  final: BadgeCheck,
  staffReview: SearchCheck,
  finalizing: PackageCheck,
  completed: CheckCircle2,
  hold: PauseCircle,
  cancelled: CircleX,
  mixed: Layers3,
}

const PHASE_NAME = {
  payment: "Payment",
  scheduled: "Scheduled",
  production: "Production",
  draft: "Draft review",
  scan: "Plagiarism scan",
  design: "Design review",
  final: "Final review",
  staffReview: "Staff review",
  finalizing: "Finalizing",
  completed: "Completed",
  hold: "On hold",
  cancelled: "Cancelled",
  mixed: "Mixed workflow",
}

// `attention` marks a state that is the customer's move without being an
// approval — a seat the order is waiting on them to fill (UPDA-105). The tier
// stays where the phase puts it (the hold grey), and the dot takes the
// needs-you colour so the pill still catches the eye.
export default function StatusBadge({ tier = "in_progress", phase, label, attention = false, className }) {
  const PhaseIcon = PHASE_ICON[phase]

  return (
    <Badge
      variant={TIER_VARIANT[tier] ?? "progress"}
      data-phase={phase}
      data-attention={attention || undefined}
      aria-label={phase ? `${label}. ${PHASE_NAME[phase]} phase.` : undefined}
      className={cn(
        "gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold tracking-[0.01em]",
        phase && "border-current/25",
        className
      )}
    >
      {PhaseIcon ? (
        <PhaseIcon className="size-3 shrink-0" strokeWidth={2.2} aria-hidden="true" />
      ) : (
        !attention && <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      )}
      {attention && <span className="size-1.5 rounded-full bg-needs-you" aria-hidden="true" />}
      {label}
    </Badge>
  )
}
